
import { useState, useEffect } from 'react';

interface User {
  id: string;
  email: string;
  full_name: string;
  role: 'admin' | 'operator';
  permissions: any;
  is_active: boolean;
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verificar si hay una sesión guardada
    const checkSavedSession = async () => {
      try {
        const savedUser = localStorage.getItem('admin_user');
        const rememberMe = localStorage.getItem('remember_me');
        const sessionUser = sessionStorage.getItem('admin_user');
        
        let userData = null;
        
        // Priorizar sesión recordada, luego sesión temporal
        if (savedUser && rememberMe === 'true') {
          userData = JSON.parse(savedUser);
        } else if (sessionUser) {
          userData = JSON.parse(sessionUser);
        }
        
        if (userData) {
          // Validar que la sesión siga siendo válida
          const isValid = await validateUserSession(userData.email);
          if (isValid) {
            setUser(userData);
          } else {
            // Limpiar sesiones inválidas
            clearAllSessions();
          }
        }
      } catch (error) {
        console.error('Error checking saved session:', error);
        clearAllSessions();
      } finally {
        // IMPORTANTE: Siempre establecer loading en false
        setLoading(false);
      }
    };

    checkSavedSession();
  }, []);

  const validateUserSession = async (email: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          action: 'validate_session',
          email
        })
      });

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Error validating session:', error);
      return false;
    }
  };

  const clearAllSessions = () => {
    localStorage.removeItem('admin_user');
    localStorage.removeItem('remember_me');
    sessionStorage.removeItem('admin_user');
  };

  const signIn = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          action: 'validate_admin',
          email,
          password
        })
      });

      const data = await response.json();

      if (data.success && data.user) {
        setUser(data.user);
        
        // Guardar sesión según la opción "recordar"
        if (rememberMe) {
          localStorage.setItem('admin_user', JSON.stringify(data.user));
          localStorage.setItem('remember_me', 'true');
          // Limpiar sesión temporal si existe
          sessionStorage.removeItem('admin_user');
        } else {
          // Solo guardar en sessionStorage para sesión temporal
          sessionStorage.setItem('admin_user', JSON.stringify(data.user));
          // Limpiar sesión permanente si existe
          localStorage.removeItem('admin_user');
          localStorage.removeItem('remember_me');
        }
        
        return { error: null };
      } else {
        return { error: { message: data.error || 'Error de autenticación' } };
      }
    } catch (error) {
      console.error('Sign in error:', error);
      return { error: { message: 'Error de conexión. Verifique su conexión a internet.' } };
    }
  };

  const signOut = async () => {
    setUser(null);
    clearAllSessions();
    return { error: null };
  };

  const resetPassword = async (email: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          action: 'reset_password',
          email
        })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Reset password error:', error);
      return { 
        success: false, 
        error: 'Error de conexión. Verifique su conexión a internet.' 
      };
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          action: 'change_admin_password',
          email: user?.email,
          password: currentPassword,
          newPassword
        })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Change password error:', error);
      return { 
        success: false, 
        error: 'Error de conexión. Verifique su conexión a internet.' 
      };
    }
  };

  const validateSession = async () => {
    if (!user) return false;
    
    try {
      const isValid = await validateUserSession(user.email);
      
      if (!isValid) {
        await signOut();
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error validating session:', error);
      return false;
    }
  };

  return {
    user,
    loading,
    signIn,
    signOut,
    resetPassword,
    changePassword,
    validateSession,
    isAdmin: user?.role === 'admin',
    isAuthenticated: !!user
  };
}
